import { Button, MenuItem, MenuList, IconButton, Tooltip } from "@chakra-ui/react";
import { FunctionComponent, ReactElement, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit } from "react-icons/fa";

// Components
import DataTable from "../../../shared/DataTable/DataTable";
import BulkUpdateClaimsModal from "../../../shared/Modals/BulkUpdateClaimsModal";
import ExportClaimsButton from "./ExportClaimsButton";

// Utils
import { RouterPathMapping } from "../../../Router";
import { IParams } from "../../../shared/DataTable/hooks/useSearchParams";
import { getOldestClaimForAnalyst } from "../../../utils/api/claims";
import { ClaimStatus, UserRole } from "../../../utils/consts";
import { DataTableColumn, KVP, PaginatedApiResponse, SortDirection } from "../../../utils/types";

// Hooks
import usePermission from "../../../hooks/usePermission";
import useClaimsToast from "../../../hooks/useToast";
import { useAuth } from "../../../hooks/useAuth";

// Protos
import { Claim, GetOldestClaimForAnalystResponse } from "../../../utils/api/protos/messages/claims.message_pb";
import AdvancedFiltersPopover from "../../../shared/Popover/AdvancedFiltersPopover";

interface ClaimsIndexTableProps {
	claimsData: PaginatedApiResponse;
	columns: Array<DataTableColumn>;
	searchParams: URLSearchParams;
	defaultParams?: Partial<IParams>;
	assignedAnalystId?: number; // Pass analyst ID for "My Claims" tab
	cacheKey: string; // Current cacheKey with all filters/search/sort
	isLoading?: boolean;
	showAdvancedFilters?: boolean; // Flag to enable advanced filters popover

	handleFetchClaims: (cacheKey: string) => void;
	handleManualRefetchTrigger: () => void;
	handleToggleLoading: (isPageLoading: boolean) => void;
}

const defaultClaimsParams: Partial<IParams> = {
	sortDirection: SortDirection.Desc,
	sortField: "createdAt"
};

const ClaimsIndexTable: FunctionComponent<ClaimsIndexTableProps> = (props: ClaimsIndexTableProps): ReactElement => {
	const {
		claimsData,
		columns,
		searchParams,
		defaultParams,
		assignedAnalystId,
		cacheKey,
		isLoading,
		handleFetchClaims,
		handleManualRefetchTrigger,
		handleToggleLoading
	} = props;
	const [isBulkUpdateModalOpen, setIsBulkUpdateModalOpen] = useState<boolean>(false);
	const [selectedBenefitCodes, setSelectedBenefitCodes] = useState<Array<string>>(() => {
		const storedBenefits = localStorage.getItem("advancedFilters.selectedBenefits");

		if (!storedBenefits) return [];

		try {
			const parsedBenefits: unknown = JSON.parse(storedBenefits);
			return Array.isArray(parsedBenefits) && parsedBenefits.every((benefit) => typeof benefit === "string")
				? parsedBenefits
				: [];
		} catch {
			return [];
		}
	});
	const [selectedClaims, setSelectedClaims] = useState<Array<KVP>>([]);
	const toast = useClaimsToast();
	const navigate = useNavigate();
	const { hasGeneralUpdateAccess, hasGeneralAdminAccess } = usePermission();
	const { getUser } = useAuth();
	const user = getUser();
	const userRoles = (user?.role?.split(",") as UserRole[]) ?? [];

	// Check if user is Claims Manager or Payments
	const isClaimsManager = userRoles.includes(UserRole.ClaimsManager);
	const isPayments = userRoles.includes(UserRole.Payments);
	const additionalSearchModel = useMemo(
		() => ({ benefitCodes: selectedBenefitCodes.join("|") }),
		[selectedBenefitCodes]
	);

	const handleAssignToAnalyst = async (): Promise<void> => {
		handleToggleLoading(true);

		await getOldestClaimForAnalyst()
			.then((response: GetOldestClaimForAnalystResponse) => {
				const assignedClaim: Claim | undefined = response.getClaim();
				if (assignedClaim) {
					const assignedClaimId: number = assignedClaim.getId();
					toast(undefined, `You have been assigned Claim #${assignedClaimId}`, "success");
				} else {
					console.error("Successfully assigned Analyst to Claim but did not receive response!");
					toast("error", "There was a problem assigning a Claim", "error");
				}
				handleManualRefetchTrigger();
			})
			.catch((err) => {
				console.error(err);
				toast("Error", err, "error");
			})
			.finally(() => {
				handleToggleLoading(false);
			});
	};

	const getCustomHeader = (): ReactElement => {
		return (
			<>
				<Button
					marginRight={4}
					size="sm"
					onClick={() => navigate(RouterPathMapping.ClaimCreate)}
					isDisabled={!hasGeneralAdminAccess}
					colorScheme="orange"
					padding={2}>
					Add Claim
				</Button>
				<Button
					marginRight={4}
					size="sm"
					onClick={() => handleAssignToAnalyst()}
					isDisabled={!hasGeneralUpdateAccess}
					colorScheme="orange"
					padding={2}>
					Take a Claim
				</Button>
			</>
		);
	};

	const getCustomRightActions = (selectedRows: Array<KVP>): ReactElement => {
		// Extract IDs from selected rows for export
		const selectedClaimIds = selectedRows.map((claim) => claim.id as number);

		return (
			<>
				<ExportClaimsButton
					cacheKey={cacheKey}
					assignedAnalystId={assignedAnalystId}
					selectedClaimIds={selectedClaimIds}
					onExport={handleManualRefetchTrigger}
				/>
				{props.showAdvancedFilters && <AdvancedFiltersPopover onFilters={setSelectedBenefitCodes} />}
			</>
		);
	};

	const getCustomSelectedActions = (selectedRows: Array<KVP>, clearSelectedRows: () => void): ReactElement => {
		// Update the selected claims state whenever selection changes
		if (
			selectedRows.length !== selectedClaims.length ||
			selectedRows.some((row, index) => row.id !== selectedClaims[index]?.id)
		) {
			setSelectedClaims(selectedRows);
		}

		if (isClaimsManager || isPayments) {
			return (
				<Tooltip label="Bulk Edit Selected Claims">
					<IconButton
						aria-label="Bulk Edit Claims"
						icon={<FaEdit />}
						size="sm"
						colorScheme="orange"
						onClick={() => setIsBulkUpdateModalOpen(true)}
						isDisabled={selectedRows.length === 0}
					/>
				</Tooltip>
			);
		}
		return <></>;
	};

	const handleBulkUpdateSuccess = (): void => {
		setSelectedClaims([]);
		handleManualRefetchTrigger();
	};

	const handleBulkUpdateClose = (): void => {
		setIsBulkUpdateModalOpen(false);
		setSelectedClaims([]);
	};

	const getCustomActionMenuActions = (claimsRow: KVP): ReactElement | undefined => {
		const claimsStatus = claimsRow["status"] as ClaimStatus;
		if (claimsStatus === ClaimStatus.Draft)
			return (
				<MenuList>
					<MenuItem onClick={() => navigate(`/claim/${claimsRow["id"]}/detail`)}>View</MenuItem>
					{claimsRow["status"] === ClaimStatus.Draft && (
						<MenuItem onClick={() => navigate(`/claim/${claimsRow["id"]}/intake`)}>Resume</MenuItem>
					)}
				</MenuList>
			);
	};

	return (
		<>
			<DataTable
				data={claimsData}
				columns={columns}
				refetchTrigger={handleFetchClaims}
				customHeaderActions={getCustomHeader()}
				customRightActions={getCustomRightActions}
				customSelectedActions={getCustomSelectedActions}
				customActionsMenuContents={getCustomActionMenuActions}
				defaultParams={defaultParams || defaultClaimsParams}
				additionalSearchModel={additionalSearchModel}
				existingParams={searchParams}
				shouldUpdateUrl={true}
				isLoading={isLoading}
			/>
			<BulkUpdateClaimsModal
				userRoles={userRoles}
				isOpen={isBulkUpdateModalOpen}
				onClose={handleBulkUpdateClose}
				selectedClaims={selectedClaims}
				onSuccess={handleBulkUpdateSuccess}
			/>
		</>
	);
};

export default ClaimsIndexTable;
